import { useMemo } from 'react'
import { DateTime } from 'luxon'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import {
  useFetchResponse,
  useEndDateForFinancialInsights,
  useReselector,
} from '@/utils/sharedHooks'
import {
  FETCH_INSIGHTS_UNCATEGORIZED_TRANSACTIONS_KEY,
  fetchInsightsUncategorizedTransactionsData,
} from './financeActions'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { getIsFetching } from '@/reducers/fetch'
import { selectIsInUnservicedFreeTrial } from '@/selectors/user.selectors'
import { Alert, Link, Text, Loader } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'

export const UncategorizedTransactionBanner = ({
  inputDate,
  practiceInsightsPage,
}: {
  inputDate?: string
  practiceInsightsPage?: boolean
}) => {
  const isInUnservicedFreeTrial = useReselector(selectIsInUnservicedFreeTrial)

  const currentDate = inputDate
    ? DateTime.fromFormat(inputDate, DATE_FORMATS_LUXON.INPUT)
    : DateTime.now()

  const insightsEndDate = useEndDateForFinancialInsights(currentDate)

  const { endDate, month, year } = useMemo(
    () => ({
      endDate: insightsEndDate.toFormat(DATE_FORMATS_LUXON.TIMESTAMP),
      month: insightsEndDate.toFormat(DATE_FORMATS_LUXON.MONTH),
      year: insightsEndDate.year,
    }),
    [insightsEndDate]
  )

  const uncategorizedTransactionData = useFetchResponse(
    fetchInsightsUncategorizedTransactionsData,
    endDate
  )

  const isLoading = useReselector(
    getIsFetching,
    FETCH_INSIGHTS_UNCATEGORIZED_TRANSACTIONS_KEY
  )

  if (isLoading)
    return (
      <div className="w-full">
        <Loader active />
      </div>
    )

  if (!uncategorizedTransactionData?.total_transactions) return null

  const transactionCount =
    Number(uncategorizedTransactionData.total_transactions) || 0
  const totalAmount = formatCurrencyFromCents(
    uncategorizedTransactionData.total_amount_in_cents || 0
  )
  const needsReview = uncategorizedTransactionData?.needs_review
  const linkTo = needsReview
    ? '/transactions/review'
    : '/transactions?categoryId=-1'
  const linkText = needsReview ? 'Review Transactions' : 'View Transactions'

  const secondSentenceText = () => {
    if (isInUnservicedFreeTrial) {
      return 'Categorize your transactions to include them.'
    }
    const baseText =
      'These numbers might change after our bookkeeping team has categorized them.'
    return needsReview
      ? `${baseText} There are some transactions that need your review.`
      : baseText
  }

  const commonText = (
    <>
      You have <b>{transactionCount} uncategorized</b> transaction
      {transactionCount > 1 ? 's ' : ' '}
      (totaling {totalAmount}) not included on this page
    </>
  )
  if (practiceInsightsPage) {
    return (
      <Alert variant="warning" title="Clarify your transactions">
        <Text
          variant={TypographyStyles.BODY_SM}
          color={TextColors.MUTED}
          className="my-2"
        >
          {commonText}. These numbers might change after our bookkeeping team
          has categorized them.
        </Text>
        <Link
          href={linkTo}
          trackMessage="view-uncategorized-transactions"
          trackAttributes={{ screen: 'practice insights' }}
          newPage={false}
        >
          View Transactions
        </Link>
      </Alert>
    )
  }

  return (
    <Alert>
      <Text
        variant={TypographyStyles.BODY_SM}
        color={TextColors.MUTED}
        className="mb-2"
      >
        {commonText}, up to {month} {year}. {secondSentenceText()}
      </Text>
      <Link href={linkTo} newPage={false}>
        {linkText}
      </Link>
    </Alert>
  )
}
